if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };

        this.mql = window.matchMedia('(min-width: 750px)');
        this.mobileSnapTimer = null;

        if (this.elements.viewer) {
          const originalIsSlideVisible = this.elements.viewer.isSlideVisible;
          this.elements.viewer.isSlideVisible = function (element, offset = 0) {
            if (window.matchMedia('(min-width: 750px)').matches) {
              return false;
            }
            return originalIsSlideVisible.call(this, element, offset);
          };

          const slider = this.elements.viewer.querySelector('.product__media-list');
          if (slider) {
            slider.addEventListener('scroll', () => {
              if (this.mql.matches) return;

              clearTimeout(this.mobileSnapTimer);
              this.mobileSnapTimer = setTimeout(() => {
                this.snapMobileSlider();
              }, 120);
            });
          }
        }

        const prevButton = this.elements.viewer ? this.elements.viewer.querySelector('button[name="previous"]') : null;
        const nextButton = this.elements.viewer ? this.elements.viewer.querySelector('button[name="next"]') : null;

        if (prevButton && nextButton) {
          prevButton.addEventListener(
            'click',
            (event) => {
              if (this.mql.matches) {
                event.preventDefault();
                event.stopImmediatePropagation();
                this.navigateGallery(-1);
              }
            },
            true
          );

          nextButton.addEventListener(
            'click',
            (event) => {
              if (this.mql.matches) {
                event.preventDefault();
                event.stopImmediatePropagation();
                this.navigateGallery(1);
              }
            },
            true
          );
        }

        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));

        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });

        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      snapMobileSlider() {
        const slider = this.elements.viewer.querySelector('.product__media-list');
        if (!slider) return;

        const mediaItems = Array.from(slider.querySelectorAll(':scope > li')).filter(
          (li) => !li.classList.contains('item--invisible') && !li.classList.contains('product__media-item--invisible')
        );

        if (!mediaItems.length) return;

        const sliderLeft = slider.scrollLeft;
        let closestItem = mediaItems[0];
        let closestDistance = Math.abs(mediaItems[0].offsetLeft - sliderLeft);

        mediaItems.forEach((item) => {
          const distance = Math.abs(item.offsetLeft - sliderLeft);
          if (distance < closestDistance) {
            closestDistance = distance;
            closestItem = item;
          }
        });

        mediaItems.forEach((item) => item.classList.remove('is-active'));
        closestItem.classList.add('is-active');

        slider.scrollTo({
          left: closestItem.offsetLeft,
          behavior: 'smooth',
        });

        if (this.elements.thumbnails && closestItem.dataset.mediaId) {
          const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${closestItem.dataset.mediaId}"]`);
          this.setActiveThumbnail(thumbnail);
        }
      }

      navigateGallery(direction) {
        const mediaItems = Array.from(this.elements.viewer.querySelectorAll('.product__media-list > li')).filter(
          (li) => !li.classList.contains('item--invisible') && !li.classList.contains('product__media-item--invisible')
        );

        if (mediaItems.length <= 1) return;

        const activeMedia = this.elements.viewer.querySelector('.is-active') || mediaItems[0];
        const activeIndex = mediaItems.indexOf(activeMedia);
        if (activeIndex === -1) return;

        let targetIndex = activeIndex + direction;

        if (targetIndex < 0) {
          targetIndex = mediaItems.length - 1;
        } else if (targetIndex >= mediaItems.length) {
          targetIndex = 0;
        }

        const targetMedia = mediaItems[targetIndex];

        if (targetMedia && targetMedia.dataset.mediaId) {
          this.setActiveMedia(targetMedia.dataset.mediaId, false);
        }
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');

        if (!activeMedia) return;

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });

        activeMedia.classList.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();

        window.setTimeout(() => {
          activeMedia.parentElement.scrollTo({
            left: activeMedia.offsetLeft,
            behavior: this.mql.matches ? 'auto' : 'smooth',
          });

          const activeMediaRect = activeMedia.getBoundingClientRect();
          if (activeMediaRect.top > -0.5) return;

          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });

        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;

        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);

        if (activeThumbnail) {
          this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);

        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;

        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };

        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}